import { roundConfig } from './round-config'
import { postcodeStart, type Job, type HowOften } from '../functions/lib/price-list'
import type { Lead } from './types'

export type JobDraft = Omit<Job, 'how_often'> & { how_often: HowOften | null; postcode: string }

// The postcode at the end of an address ("14 Fortis Green, London N10 3AB" -> "N10 3AB"), or just the
// first half when that is all there is ("... N10" -> "N10"). Null when there is none.
export function postcodeFromAddress(address: string | null | undefined): string | null {
  const a = String(address ?? '').trim()
  if (!a) return null
  // A US round: the ZIP at the end ("Austin, TX 78701-2345" -> "78701"); Canada: the postal code (Step 2 round 3).
  const country = roundConfig().country
  if (country === 'US') return /(?:^|[\s,])(\d{5})(?:-\d{4})?\s*$/.exec(a)?.[1] ?? null
  if (country === 'CA') { const ca = /(?:^|[\s,])([A-Z]\d[A-Z])\s*(\d[A-Z]\d)\s*$/i.exec(a); return ca ? `${ca[1]} ${ca[2]}`.toUpperCase() : null }
  const full = a.match(/\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/gi)
  if (full?.length) return full[full.length - 1]!.toUpperCase().replace(/\s+/g, ' ')
  const half = /(?:^|[\s,])([A-Z]{1,2}\d[A-Z\d]?)\s*$/i.exec(a)
  return half && postcodeStart(half[1], roundConfig().country) ? half[1]!.toUpperCase() : null
}

export function jobFromLead(lead: Pick<Lead, 'address' | 'cycle_weeks'> & { cycle_text?: string | null }): JobDraft {
  const c = lead.cycle_weeks
  return {
    windows: null,
    size: null,
    postcode: postcodeFromAddress(lead.address) ?? '',
    roof_windows: null,
    how_often: c === 2 ? 'w2' : c === 4 ? 'w4' : c === 6 ? 'w6' : c === 8 ? 'w8' : c === 12 ? 'w12' : lead.cycle_text ? 'one_off' : null,
    fronts_only: false,
  }
}

const NUMBER_WORDS: Record<string, number> = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, thirteen: 13, fourteen: 14, fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18, nineteen: 19, twenty: 20 }
export function windowsFromWords(words: string | null | undefined): number | null {
  const text = String(words ?? '').toLowerCase().replace(/\b(?:\d+|one|two|three|four|five|six|seven|eight|nine|ten)\s*[- ]?bed(?:room)?s?\b(?=\s*(?:[,.;!?]|$)|\s+(?:house|home|flat|property)\b)/g, '')
  const numbers = text.match(new RegExp(`\\d+|\\b(?:${Object.keys(NUMBER_WORDS).join('|')})\\b`, 'g')) ?? []
  if (numbers.length !== 1) return null
  const hit = new RegExp(`(?:^|[^\\w])(\\d+|${Object.keys(NUMBER_WORDS).join('|')})\\s*(?:x\\s*)?windows?\\b`).exec(text)
  if (!hit) return null
  const n = /^\d+$/.test(hit[1]!) ? Number(hit[1]) : NUMBER_WORDS[hit[1]!]!
  return n >= 1 && n <= 200 ? n : null
}

